import React from 'react';

/**
 * PrintSheetV4 — 실치수(A4/B4 실측 규격) 시험지 페이지 렌더러.
 *
 * v2(printPreview) 시각 구성을 유지하되 좌표계가 실제 용지 mm다:
 *  문제 페이지(첫장 bg_test 헤더/2단/푸터) → (홀수면 빈 페이지) → 빠른정답 → 풀이.
 * 모든 클래스는 pv4- 접두 — 레거시 인쇄 CSS(전역 주입분)와의 충돌 방지.
 * 구조 상수는 전부 geo(printSpec.getSheetGeometry)에서 파생 — 하드코딩 금지.
 */

const pad2 = (n) => String(n).padStart(2, '0');
const MM_PER_PT = 25.4 / 72;

// A4 헤더 이름 밑줄 폭(mm) — 학생명이 있으면 "이름" 라벨 오른쪽 끝을 기준으로 이름이
// 좌우 대칭이 되는 길이로 맞추고, 없으면 손으로 적는 칸이므로 기본 50mm를 유지한다.
//   라벨 폭 = 10pt × 2자 = 7.06 → 이름 좌측 여백 = 15(이름 시작) − 7.06 = 7.94
//   width = 15 + (11pt × 글자수) + 7.94   (3자 34.6 / 2자 30.7 / 4자 38.5)
// 전제: Pretendard 한글은 전각(1글자 = 폰트 크기). 영문 이름은 실제보다 길게 잡힌다.
const NAME_LINE_BLANK_W = 50;
function nameLineWidthMm(studentName) {
  const len = String(studentName || '').trim().length;
  if (!len) return NAME_LINE_BLANK_W;
  const gapLeft = 15 - 2 * 10 * MM_PER_PT;
  return 15 + len * 11 * MM_PER_PT + gapLeft;
}
// 이름 텍스트 top → 밑줄까지의 간격 (A4 실측 좌표 56.6 − 50.2). B4 이름 필드도 같은 간격을 쓴다.
const NAME_LINE_DROP = 6.4;
// 라벨(10pt)과 이름(11pt)을 같은 top에 두면 글자 크기 차이만큼 기준선이 어긋나 아랫선이 안 맞는다.
// Pretendard 실측(4배 확대 렌더, 상자 top 기준): 라벨 기준선 3.4396mm · 이름 기준선 3.7042mm.
// 이름 상자만 그 차이만큼 올려 두 글자의 아랫선을 맞춘다 — 라벨과 밑줄 위치는 그대로다.
const NAME_BASELINE_LIFT = 0.2646;
// A4/B4 공용 이름 필드 — 라벨(10pt) + 이름(11pt, 라벨 기준 +15mm) + 밑줄(0.4mm).
// 블록 폭 = nameLineWidthMm이라 좌/우 어느 쪽에 붙이든 내부 구성이 동일하다.
function NameField({ studentName }) {
  return (
    <>
      <div style={{ position: 'absolute', left: 0, top: 0, fontSize: '10pt', fontWeight: 700, color: '#111' }}>
        이름
      </div>
      <div style={{ position: 'absolute', left: '15mm', top: `-${NAME_BASELINE_LIFT}mm`, fontSize: '11pt', fontWeight: 700, color: '#111' }}>
        {studentName || ''}
      </div>
      <div style={{ position: 'absolute', left: 0, top: `${NAME_LINE_DROP}mm`, width: '100%', borderBottom: '0.4mm solid #333' }} />
    </>
  );
}

// A4 리뉴얼 푸터 구분줄의 paper Y 좌표(시안 실측) — 푸터·세로 구분선 연장이 공유
const RENEWAL_FOOTER_LINE_PAPER_Y = 281.3;

// B4 리뉴얼(수능형) 시안 실측 상수 (paper mm)
// 가로줄도 세로줄(::after width+background)과 동일하게 height+background로 렌더 —
// border 방식은 픽셀 스냅이 달라 같은 mm여도 굵기가 달라 보인다.
const B4_RULE_W = '0.41mm';
const B4_RULE_COLOR = '#1a1a1a';
const B4_RULE_FIRST_Y = 62.5;  // 첫 장 굵은 가로줄
const B4_RULE_REST_Y = 30.9;   // 이후 장 굵은 가로줄
const B4_GAP_FIRST = 5.0; // 가로줄→문제 기본 시작 간격 = 세로줄 상단 연장량 (첫 장)
const B4_GAP_REST = 5.0;  // 〃 (이후 장) — 마커가 붙은 문제만 밴드만큼 추가로 내려감
const B4_MARKER_BAND = 10.5; // 유형 마커 밴드 높이 = 마커가 붙은 문제의 시프트량
// 첫 장 헤더 세로 위치(paper mm). 시안 실측(29.2/48.8) 대비:
//  1) 회차줄을 2.5mm 올려 대제목과의 간격을 좁히고(간격 = 두 값의 차이 19.6 → 17.1),
//  2) 블록 위치는 시안 그대로 둔다(대제목 29.2 유지 — 블록 이동 = 두 값을 같은 크기로 증감).
const B4_TITLE_Y = 29.2;  // 대제목(기본 "수학 영역", 40pt) — 시안 실측 유지
const B4_LINE2_Y = 46.3;  // 하단 회차줄(16pt 회색) — 시안 48.8 → −2.5
// "제 2교시" 캡슐(첫 장 좌측). 우측 이름 필드가 이 박스의 아래 외곽선에 밑줄을 맞추므로
// 값을 상수로 묶어 단일 소스로 둔다 — 캡슐을 손대면 이름 필드도 자동으로 따라온다.
const B4_PERIOD_Y = 37.5;        // 캡슐 top(paper)
const B4_PERIOD_BORDER = 0.35;   // 테두리 두께(mm)
const B4_PERIOD_PAD_Y = 1.2;     // 상하 패딩(mm)
const B4_PERIOD_FONT_PT = 14;
const B4_PERIOD_LINE_H = 1.2;
// 캡슐 아래 외곽선 = top + (테두리+패딩)×2 + 본문 줄높이 → 46.53mm
const B4_PERIOD_BOTTOM_Y =
  B4_PERIOD_Y + 2 * (B4_PERIOD_BORDER + B4_PERIOD_PAD_Y) + B4_PERIOD_FONT_PT * B4_PERIOD_LINE_H * MM_PER_PT;

// 유형 마커 박스(시안 스타일). 지정한 문제 바로 위에 붙는다.
function B4MarkerBox({ label }) {
  return (
    <div style={{ display: 'inline-block', border: '0.35mm solid #111', padding: '1mm 6mm', fontSize: '13pt', fontWeight: 500, color: '#111', lineHeight: 1.3, background: '#fff', fontFamily: "'WantedSans', 'Pretendard', sans-serif" }}>
      {label}
    </div>
  );
}

// meta.b4Markers([{label, problemNo:number|null}]) → { [문제번호]: label }
// 페이지·칼럼 좌표가 아니라 문제 번호로 지정하므로, 문항 수·높이가 바뀌어 재배치돼도
// 마커가 지정한 문제를 따라간다.
function markersByProblem(meta) {
  const out = {};
  (meta.b4Markers || []).forEach((m) => {
    if (!m || m.problemNo == null) return;
    out[m.problemNo] = m.label;
  });
  return out;
}

/**
 * 시트 공통 CSS 문자열 — 렌더(SheetChrome)와 오프스크린 측정(measureV4)이 같은 소스를
 * 쓴다. 측정 시 이 CSS가 함께 주입되지 않으면 이미지/박스 마진·폰트 강제가 빠져
 * 측정↔렌더 높이가 어긋나 슬롯 겹침이 생긴다(리뷰 확정 결함) — 반드시 공유할 것.
 */
export function sheetChromeCss(geo) {
  return `
      /* B4 헤더 전용 폰트 — 자체 호스팅(public/fonts/, TTF). 파일 부재 시 Pretendard 폴백 */
      @font-face {
        font-family: 'NanumSquareRound';
        src: url('${process.env.PUBLIC_URL}/fonts/NanumSquareRoundR.ttf') format('truetype');
        font-weight: 400; font-style: normal; font-display: swap;
      }
      @font-face {
        font-family: 'NanumSquareRound';
        src: url('${process.env.PUBLIC_URL}/fonts/NanumSquareRoundB.ttf') format('truetype');
        font-weight: 700; font-style: normal; font-display: swap;
      }
      /* B4 수능형 전용 폰트 3종 */
      @font-face {
        font-family: 'HanamDaum';
        src: url('${process.env.PUBLIC_URL}/fonts/HANAMDAUM(Windows).ttf') format('truetype');
        font-weight: 400; font-style: normal; font-display: swap;
      }
      /* KoPub은 굵기별 파일이 따로다 — 본문(400)은 Medium, 제목·번호(700)는 Bold.
         @font-face weight = 실제 사용 weight를 일치시켜야 브라우저 합성 볼드가 끼지 않는다. */
      @font-face {
        font-family: 'KoPubWorldBatang';
        src: url('${process.env.PUBLIC_URL}/fonts/KoPubWorld Batang Medium.ttf') format('truetype');
        font-weight: 400; font-style: normal; font-display: swap;
      }
      @font-face {
        font-family: 'KoPubWorldBatang';
        src: url('${process.env.PUBLIC_URL}/fonts/KoPubWorld Batang Bold.ttf') format('truetype');
        font-weight: 700; font-style: normal; font-display: swap;
      }
      /* 표지 본문용 고딕(오너 시안 2026-09-09) — Batang과 같은 굵기별 파일 구성 */
      @font-face {
        font-family: 'KoPubWorldDotum';
        src: url('${process.env.PUBLIC_URL}/fonts/KoPubWorld Dotum Medium.ttf') format('truetype');
        font-weight: 400; font-style: normal; font-display: swap;
      }
      @font-face {
        font-family: 'KoPubWorldDotum';
        src: url('${process.env.PUBLIC_URL}/fonts/KoPubWorld Dotum Bold.ttf') format('truetype');
        font-weight: 700; font-style: normal; font-display: swap;
      }
      @font-face {
        font-family: 'WantedSans';
        src: url('${process.env.PUBLIC_URL}/fonts/WantedSans-Medium.ttf') format('truetype');
        font-weight: 500; font-style: normal; font-display: swap;
      }
      .pv4-sheet { background: #fff; }
      .pv4-page {
        position: relative;
        width: ${geo.paper.w}mm;
        height: ${geo.paper.h}mm;
        background: #fff;
        box-sizing: border-box;
        overflow: hidden;
        margin: 0 auto 10px;
        box-shadow: 0 0 10px rgba(0,0,0,0.3);
      }
      .pv4-content-box {
        position: absolute;
        top: ${geo.inset.top}mm; left: ${geo.inset.left}mm;
        right: ${geo.inset.right}mm; bottom: ${geo.inset.bottom}mm;
      }
      .pv4-problem-content *:not(.katex):not(.katex *) {
        font-family: ${geo.design === 'renewalB4' ? "'KoPubWorldBatang', 'Pretendard', serif" : "'Pretendard', sans-serif"} !important;
        font-weight: 400 !important;
        font-size: ${geo.contentFontPt}pt !important;
      }
      .pv4-problem-content { line-height: 1.5; }
      .pv4-sheet .katex { font-size: 1em !important; }
      /* 빠른정답 셀: KaTeX displayMode 기본 마진(1em 0)이 행높이를 12mm+로 키워
         25행이 페이지를 초과함(실측 리뷰) — 정답 셀에서만 리셋 */
      .pv4-sheet .pv4-answer-cell .katex-display { margin: 0; }
      .pv4-problem-content img { max-width: 100%; height: auto; display: block; margin: ${8 * geo.k}mm auto; }
      .pv4-problem-content .PlaygroundEditorTheme__layoutContainer {
        display: grid; grid-template-columns: 1fr 1fr; gap: ${16 * geo.k}mm;
        max-width: 100%; margin: ${8 * geo.k}mm 0; break-inside: avoid;
      }
      .pv4-problem-content .PlaygroundEditorTheme__layoutItem {
        width: 100%; min-width: 0; padding: 0; box-sizing: border-box;
        overflow-wrap: break-word; border: none !important;
      }
      .pv4-problem-content .PlaygroundEditorTheme__layoutItem > * { margin: ${2 * geo.k}mm 0; }
      .pv4-problem-content .PlaygroundEditorTheme__paragraph { margin: 0; position: relative; }
      /* 객관식 선지 등 Lexical 레이아웃 컨테이너 — 클래스 없이 data 속성만 달고 나오며,
         display:grid는 이 규칙이 부여하고 실제 칼럼 수(1fr×N)는 인라인 style이 덮는다(v2 동일). */
      .pv4-problem-content [data-lexical-layout-container] {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(3, auto);
        max-width: 100%;
      }
      .pv4-problem-content .PlaygroundEditorTheme__imageContainer { text-align: center; margin: ${8 * geo.k}mm 0; }
      .pv4-problem-content .PlaygroundEditorTheme__image { display: inline-block; max-width: 100%; vertical-align: middle; }
      /* 상자(<보기>) — 대상은 바깥 틀 .box-container 다(BE-20260925 F2). 종전 선택자 __boxContent 는
         BoxContentNode.importJSON 이 className 을 버려 HTML 에 한 번도 나오지 않아 이 규칙이 죽어 있었고,
         상자는 편집기 CSS(BoxPlugin.css .box-container — px 값)를 빌려 그려졌다. 안쪽 .box-content 에 걸면
         바깥 틀의 테두리와 겹쳐 두 겹이 된다. 값(2.6k mm)은 종전 그대로 — 이제 실제로 걸린다. */
      .pv4-problem-content .box-container {
        border: 1px solid black; padding: ${2.6 * geo.k}mm; margin: ${2.6 * geo.k}mm auto;
        max-width: 100%; border-radius: 4px; background: #fff; break-inside: avoid;
      }
      .pv4-problem-content .math-element {
        font-size: 0.91em; display: inline-block; vertical-align: middle; margin: 0 2px;
      }
      .pv4-problem-content p { overflow-wrap: break-word; word-break: break-word; }
      .pv4-columns { position: relative; display: flex; gap: ${geo.colGap}mm; }
      .pv4-columns::after {
        content: ''; position: absolute; top: 0; left: 50%; height: 100%;
        width: 1px; background: #ccc; transform: translateX(-50%);
      }
      ${geo.design === 'renewalA4' ? `
      /* A4 리뉴얼(문제 페이지): 세로 구분선을 푸터 구분줄과 동일 규격(0.2mm #ccc)으로 맞추고,
         푸터 가로줄(paper ${RENEWAL_FOOTER_LINE_PAPER_Y}mm)까지 연장해 직교로 연결 */
      .pv4-problem-cols::after {
        width: 0.2mm;
        height: calc(100% + ${(RENEWAL_FOOTER_LINE_PAPER_Y - geo.inset.top) - (geo.contentH - geo.footerH)}mm);
      }` : ''}
      ${geo.design === 'renewalB4' ? `
      /* B4 리뉴얼(수능형): 세로 구분선을 굵은 검정(0.41mm)으로, 위로 연장해 헤더 가로줄에 접합 */
      .pv4-b4-first.pv4-problem-cols::after {
        width: 0.41mm; background: #1a1a1a;
        top: -${B4_GAP_FIRST}mm; height: calc(100% + ${B4_GAP_FIRST}mm);
      }
      .pv4-b4-rest.pv4-problem-cols::after {
        width: 0.41mm; background: #1a1a1a;
        top: -${B4_GAP_REST}mm; height: calc(100% + ${B4_GAP_REST}mm);
      }` : ''}
      .pv4-column { position: relative; width: ${geo.colW}mm; flex: none; }
    `;
}

function SheetChrome({ geo }) {
  return <style>{sheetChromeCss(geo)}</style>;
}

/**
 * A4 리뉴얼 헤더 (2026-08 신디자인, 참고 PNG 픽셀 실측 — paper 좌표에서 데드존을 뺀
 * content-box 좌표로 배치). 배경 패턴·QR 제거, 텍스트+로고만.
 * 좌: edu_ver(회색) / tree_name(녹색 대제목) / 커스텀명(회색 부제, 없으면 생략) / 이름 필드
 * 우: TID + 신 로고(logo_header_a4.png)
 */
function HeaderRenewal({ geo, meta }) {
  const inner = geo.frameInset; // 디자인 기준선(paper 14mm) — 문제 영역·푸터와 단일 소스
  const y = (paperMm) => paperMm - geo.inset.top; // paper mm → content-box mm
  return (
    <div style={{ position: 'relative', height: `${geo.headerFirstH}mm`, fontFamily: "'Pretendard', sans-serif" }}>
      {/* 좌측 텍스트 블록 */}
      <div style={{ position: 'absolute', left: `${inner}mm`, top: `${y(13.6)}mm`, fontSize: '10pt', color: '#888' }}>
        {meta.edu_ver}
      </div>
      <div style={{ position: 'absolute', left: `${inner}mm`, top: `${y(21.6)}mm`, fontSize: '26pt', fontWeight: 900, color: '#4ABC85', lineHeight: 1.15, whiteSpace: 'nowrap' }}>
        {meta.tree_name}
      </div>
      {meta.customName && (
        <div style={{ position: 'absolute', left: `${inner}mm`, top: `${y(34.0)}mm`, fontSize: '12pt', fontWeight: 600, color: '#8c8c8c', whiteSpace: 'nowrap' }}>
          {meta.customName}
        </div>
      )}
      {/* 이름 필드 — 학생명 없으면 빈 밑줄. 폭은 글자수에 맞춰 좌우 대칭(nameLineWidthMm) */}
      <div style={{ position: 'absolute', left: `${inner}mm`, top: `${y(50.2)}mm`, width: `${nameLineWidthMm(meta.student_name)}mm` }}>
        <NameField studentName={meta.student_name} />
      </div>
      {/* 우측 로고 — 폭 39mm(시안 실측), 세로 중심을 제목 중심(paper 26.7mm)에 정렬 */}
      <img
        src={`${process.env.PUBLIC_URL}/assets/images/print_v4/logo_header_a4.png`}
        alt="KY TEST"
        style={{ position: 'absolute', right: `${inner}mm`, top: `${y(26.7)}mm`, transform: 'translateY(-50%)', width: '39mm', height: 'auto' }}
      />
      {/* TID — 로고의 ㄱ자(좌측 바) 위에 로고와 겹쳐 배치(시안: TID 좌측선 = 로고 좌측선) */}
      <div style={{ position: 'absolute', left: `${geo.contentW - inner - 39}mm`, top: `${y(13.1)}mm`, fontSize: '10pt', color: '#888', zIndex: 1 }}>
        {meta.idLabel || 'TID'}. {meta.exam_id}
      </div>
    </div>
  );
}

/** A4 리뉴얼 푸터 — 본문과 사이 가로줄(실측 paper 281.3mm, #ccc) + 좌 edu_ver·tree_name / 중앙 페이지번호 / 우 학원 로고 */
function FooterRenewal({ geo, pageNo, edu_ver, tree_name }) {
  const inner = geo.frameInset;
  const lineBottom = geo.contentH - (RENEWAL_FOOTER_LINE_PAPER_Y - geo.inset.top); // 실측 가로줄 위치 → content 하단 기준
  return (
    <>
    <div style={{ position: 'absolute', left: `${inner}mm`, right: `${inner}mm`, bottom: `${lineBottom}mm`, height: '0.2mm', background: '#ccc' }} />
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: '1.3mm', height: '5.2mm', fontFamily: "'Pretendard', sans-serif" }}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center' }}>
        <span style={{ position: 'absolute', left: `${inner}mm`, fontSize: '9pt', color: '#888' }}>
          {edu_ver} · {tree_name}
        </span>
        <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: '13pt', fontWeight: 800, color: '#111', lineHeight: 1 }}>
          {pad2(pageNo)}
        </span>
        <img
          src={`${process.env.PUBLIC_URL}/assets/images/print_v4/logo_academy.png`}
          alt="학원 로고"
          style={{ position: 'absolute', right: `${inner}mm`, height: '4.6mm', width: 'auto' }}
        />
      </div>
    </div>
    </>
  );
}

/**
 * B4 리뉴얼(수능형) 첫 장 헤더 — 시안 실측(paper→content 변환).
 * 좌: 로고 + "제 2교시" 캡슐 / 중앙: 입력 1줄·대제목(b4Title, 기본 "수학 영역")·입력 2줄
 * 우: 대형 페이지번호 1 + 그 아래 이름 필드(밑줄을 "제 2교시" 캡슐 아래 외곽선에 정렬)
 * 하단: 굵은 가로줄(0.41mm) + "5지선다형" 박스.
 */
function HeaderRenewalB4First({ geo, meta }) {
  const inner = geo.frameInset;
  const y = (paperMm) => paperMm - geo.inset.top;
  return (
    <div style={{ position: 'relative', height: `${geo.headerFirstH}mm`, fontFamily: "'Pretendard', sans-serif" }}>
      <img
        src={`${process.env.PUBLIC_URL}/assets/images/print_v4/logo_header_a4.png`}
        alt="KY TEST"
        style={{ position: 'absolute', left: `${inner}mm`, top: `${y(16.7)}mm`, height: '14.4mm', width: 'auto' }}
      />
      <div style={{ position: 'absolute', left: `${inner}mm`, top: `${y(B4_PERIOD_Y)}mm`, border: `${B4_PERIOD_BORDER}mm solid #111`, borderRadius: '999px', padding: `${B4_PERIOD_PAD_Y}mm 2.5mm`, fontSize: `${B4_PERIOD_FONT_PT}pt`, fontFamily: "'KoPubWorldBatang', 'Pretendard', serif", fontWeight: 700, color: '#111', lineHeight: B4_PERIOD_LINE_H }}>
        제 2교시
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${y(16.8)}mm`, textAlign: 'center', fontSize: '18pt', fontFamily: "'NanumSquareRound', 'Pretendard', sans-serif", fontWeight: 400, color: '#231F20' }}>
        {meta.b4Line1 || ''}
      </div>
      {/* 대제목 40pt — 괄호 선택과목은 시안 규칙대로 제목의 절반(20pt) */}
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${y(B4_TITLE_Y)}mm`, textAlign: 'center', fontSize: '40pt', fontFamily: "'HanamDaum', 'Pretendard', sans-serif", fontWeight: 400, color: '#111', lineHeight: 1.1 }}>
        {meta?.b4Title ?? '수학 영역'}{meta.b4Subject ? <span style={{ fontSize: '20pt' }}>({meta.b4Subject})</span> : null}
      </div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${y(B4_LINE2_Y)}mm`, textAlign: 'center', fontSize: '16pt', fontFamily: "'NanumSquareRound', 'Pretendard', sans-serif", fontWeight: 700, color: '#A3A1A2' }}>
        {meta.b4Line2 || ''}
      </div>
      <div style={{ position: 'absolute', right: `${inner}mm`, top: `${y(16.8)}mm`, fontSize: '24pt', fontFamily: "'KoPubWorldBatang', 'Pretendard', serif", fontWeight: 700, color: '#111', lineHeight: 1 }}>
        1
      </div>
      {/* 우측 이름 필드 — 페이지번호 아래. 밑줄을 좌측 "제 2교시" 캡슐의 아래 외곽선에 맞춘다
          (밑줄 = 블록 top + NAME_LINE_DROP 이므로 top을 그만큼 올려 잡는다). */}
      <div
        style={{
          position: 'absolute',
          right: `${inner}mm`,
          top: `${y(B4_PERIOD_BOTTOM_Y - NAME_LINE_DROP)}mm`,
          width: `${nameLineWidthMm(meta.student_name)}mm`,
        }}
      >
        <NameField studentName={meta.student_name} />
      </div>
      <div style={{ position: 'absolute', left: `${inner}mm`, right: `${inner}mm`, top: `${y(B4_RULE_FIRST_Y)}mm`, height: B4_RULE_W, background: B4_RULE_COLOR }} />
    </div>
  );
}

/** B4 리뉴얼 이후 장 헤더 — 대형 페이지번호·로고가 홀짝 교차(짝수: 번호 좌·로고 우 / 홀수: 반대) */
function HeaderRenewalB4Rest({ geo, pageNo, meta, titleSuffix = null }) {
  const inner = geo.frameInset;
  const y = (paperMm) => paperMm - geo.inset.top;
  const even = pageNo % 2 === 0;
  const numStyle = { position: 'absolute', top: `${y(17.8)}mm`, fontSize: '24pt', fontFamily: "'KoPubWorldBatang', 'Pretendard', serif", fontWeight: 700, color: '#111', lineHeight: 1 };
  const logoStyle = { position: 'absolute', top: `${y(16.7)}mm`, height: '10.3mm', width: 'auto' };
  return (
    <div style={{ position: 'relative', height: `${geo.headerRestH}mm`, fontFamily: "'Pretendard', sans-serif" }}>
      <div style={{ ...numStyle, ...(even ? { left: `${inner}mm` } : { right: `${inner}mm` }) }}>{pageNo}</div>
      <div style={{ position: 'absolute', left: 0, right: 0, top: `${y(17.2)}mm`, textAlign: 'center', fontSize: '22pt', fontFamily: "'HanamDaum', 'Pretendard', sans-serif", fontWeight: 400, color: '#111', lineHeight: 1.1 }}>
        {meta?.b4Title ?? '수학 영역'}{meta?.b4Subject ? <span style={{ fontSize: '11pt' }}>({meta.b4Subject})</span> : null}
        {titleSuffix ? <span style={{ fontSize: '11pt', marginLeft: '2mm' }}>{titleSuffix}</span> : null}
      </div>
      <img
        src={`${process.env.PUBLIC_URL}/assets/images/print_v4/logo_header_a4.png`}
        alt="KY TEST"
        style={{ ...logoStyle, ...(even ? { right: `${inner}mm` } : { left: `${inner}mm` }) }}
      />
      <div style={{ position: 'absolute', left: `${inner}mm`, right: `${inner}mm`, top: `${y(B4_RULE_REST_Y)}mm`, height: B4_RULE_W, background: B4_RULE_COLOR }} />
    </div>
  );
}

/** B4 리뉴얼 푸터 — 중앙에 수능식 페이지 박스(좌상 현재/대각선/우하 총), 우측 학원 로고 */
function FooterRenewalB4({ geo, pageNo, totalPages }) {
  const inner = geo.frameInset;
  const bandBottom = geo.contentH - (345.3 - geo.inset.top);
  // 두 자리 숫자(예: 12/20)까지 여유 있게 — 박스는 밴드 하단 기준으로 위로 확장
  const boxW = 16;
  const boxH = 8;
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${bandBottom}mm`, height: `${boxH}mm`, fontFamily: "'Pretendard', sans-serif" }}>
      <div style={{ position: 'relative', height: '100%' }}>
        {/* 테두리·대각선 = 프레임 선(0.41mm #1a1a1a)과 동일 규격. 숫자는 line-height:1 + 동일 pad로 상하 대칭 */}
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: 0, width: `${boxW}mm`, height: `${boxH}mm`, border: `${B4_RULE_W} solid ${B4_RULE_COLOR}`, boxSizing: 'border-box', fontFamily: "'KoPubWorldBatang', 'Pretendard', serif", fontWeight: 700, color: '#111' }}>
          <svg viewBox={`0 0 ${boxW} ${boxH}`} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
            <line x1="0" y1={boxH} x2={boxW} y2="0" stroke={B4_RULE_COLOR} strokeWidth="0.41" />
          </svg>
          <span style={{ position: 'absolute', top: '0.7mm', left: '1.6mm', fontSize: '11pt', lineHeight: 1 }}>{pageNo}</span>
          <span style={{ position: 'absolute', bottom: '0.7mm', right: '1.6mm', fontSize: '11pt', lineHeight: 1 }}>{totalPages}</span>
        </div>
        <img
          src={`${process.env.PUBLIC_URL}/assets/images/print_v4/logo_academy.png`}
          alt="학원 로고"
          style={{ position: 'absolute', right: `${inner}mm`, top: '50%', transform: 'translateY(-50%)', height: '4.6mm', width: 'auto' }}
        />
      </div>
    </div>
  );
}

function Footer({ geo, pageNo, leftFooter, edu_ver, tree_name }) {
  return (
    <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${geo.footerH}mm`, boxSizing: 'border-box', paddingTop: `${2 * geo.k}mm` }}>
      <hr style={{ border: 'none', borderTop: '1px solid #ccc', margin: `0 0 ${3 * geo.k}mm 0`, width: '100%' }} />
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: '#666', fontSize: `${geo.pt(10)}pt`, fontFamily: "'Pretendard', sans-serif", fontWeight: 'bold' }}>
        <span>{leftFooter}</span>
        <span style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', fontSize: `${geo.pt(18)}pt`, color: '#000', lineHeight: 1 }}>
          {pad2(pageNo)}
        </span>
        <span>{edu_ver} · {tree_name}</span>
      </div>
    </div>
  );
}

/**
 * 문항 번호 규격 — 디자인별 단일 소스. 문제 항목·풀이 항목·측정(measureV4)이 같은 값을 써야
 * 실측과 렌더가 일치하고, 풀이 면이 문제 면과 한 권으로 읽힌다.
 * B4(수능형): "1." 검정 KoPub 700·17pt(시안 실측) — A4: "01" 녹색 900.
 */
export function numberSpecV4(geo, number) {
  const isB4 = geo.design === 'renewalB4';
  return {
    fontPt: isB4 ? 17 : geo.pt(27),
    font: isB4 ? "'KoPubWorldBatang', 'Pretendard', serif" : "'Pretendard', sans-serif",
    weight: isB4 ? 700 : 900,
    color: isB4 ? '#111' : '#4ABC85',
    text: isB4 ? `${number}.` : pad2(number),
    gapMm: (isB4 ? 3.7 : 7.4) * geo.k, // 번호-본문 간격 — B4는 절반
  };
}

function ProblemItemV4({ problem, geo, extraTop = 0 }) {
  const spec = numberSpecV4(geo, problem.problemNumber);
  const { fontPt: numberFontPt, font: numFont, weight: numWeight, color: numColor, text: numText, gapMm } = spec;
  return (
    <div
      className="pv4-problem-item"
      /* 이름표 두 개는 **미리보기 전용**이다 — 시험지 편집 모달의 인라인 지면이 문항 클릭 ·
         교체 대상 테두리 · 「담은 목록의 순번 → 그 자리로 데려가기」를 이 두 값으로 잇는다.
         렌더되지 않는 속성이라 조판에도 인쇄 출력에도 영향이 없고, 측정이 쓰는 마크업은
         이것과 별개다(measureV4.js 는 자기 문자열로 잰다).
         문서 순서로 세는 길도 오늘은 성립하지만(두 배치 경로가 모두 오름차순) 그 불변식은
         어디에도 적혀 있지 않고 지키는 검사도 없다 — 붙어 있는 이름표가 싸다. */
      data-problem-number={problem.problemNumber}
      data-problem-id={problem.problem_id}
      style={{
        position: 'absolute',
        top: `${problem.startHeight + extraTop}mm`,
        left: 0,
        width: '100%',
        display: 'flex',
        gap: `${gapMm}mm`,
        alignItems: 'flex-start',
        overflowWrap: 'break-word',
        textAlign: 'left',
      }}
    >
      <div style={{ fontSize: `${numberFontPt}pt`, fontWeight: numWeight, color: numColor, lineHeight: 1, minWidth: `${numberFontPt}pt`, whiteSpace: 'nowrap', fontFamily: numFont }}>
        {numText}
      </div>
      <div
        className="pv4-problem-content"
        style={{ flex: 1, minWidth: 0, fontSize: `${geo.contentFontPt}pt` }}
        dangerouslySetInnerHTML={{ __html: problem.content }}
      />
    </div>
  );
}

function ProblemPage({ page, pageIndex, geo, meta, totalProblemPages, renderPageOverlay }) {
  const first = pageIndex === 0;
  const renewalA4 = geo.design === 'renewalA4';
  const renewalB4 = geo.design === 'renewalB4';
  // 유형 마커: 지정한 문제 바로 위에 박스 + 그 문제만 밴드만큼 시프트(슬롯 격자는 유지)
  const problemMarkers = renewalB4 ? markersByProblem(meta) : {};
  const areaH = first ? geo.areaFirstH : geo.areaRestH;
  return (
    <div className="pv4-page">
      {/* 미리보기 전용 오버레이(면 배열 컨트롤). absolute·pv4-no-print라 인쇄에는 존재하지 않는다 */}
      {renderPageOverlay ? renderPageOverlay(pageIndex) : null}
      <div className="pv4-content-box">
        {renewalB4 ? (
          first
            ? <HeaderRenewalB4First geo={geo} meta={meta} />
            : <HeaderRenewalB4Rest geo={geo} pageNo={pageIndex + 1} meta={meta} />
        ) : first && renewalA4 ? (
          <HeaderRenewal geo={geo} meta={meta} />
        ) : first ? (
          <div style={{
            height: `${geo.headerFirstH}mm`,
            backgroundImage: `url('${process.env.PUBLIC_URL}/assets/images/bg_test.png')`,
            backgroundSize: 'contain', backgroundPosition: 'top center', backgroundRepeat: 'no-repeat',
            position: 'relative', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact',
          }}>
            <div style={{ position: 'absolute', top: `${16 * geo.k}mm`, left: `${16 * geo.k}mm`, right: `${16 * geo.k}mm`, display: 'grid', gridTemplateColumns: '2fr 1fr', gap: `${5 * geo.k}mm`, fontFamily: "'Pretendard', sans-serif" }}>
              <div>
                <p style={{ fontSize: `${geo.pt(14)}pt`, color: '#333', margin: 0, fontWeight: 500 }}>{meta.edu_ver}</p>
                <p style={{ fontSize: `${geo.pt(32)}pt`, fontWeight: 900, margin: `${8 * geo.k}mm 0`, color: '#4ABC85' }}>{meta.tree_name}</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: `${geo.pt(14)}pt`, margin: 0, whiteSpace: 'nowrap' }}>{meta.idLabel || 'TID'}. {meta.exam_id}</p>
                {meta.student_name && (
                  <p style={{ fontSize: `${geo.pt(18)}pt`, fontWeight: 'bold', marginTop: `${65 * geo.k}mm`, color: '#000', whiteSpace: 'nowrap' }}>{meta.student_name}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ height: `${geo.headerRestH + geo.restTopGap}mm` }} />
        )}

        {/* 리뉴얼: 문제 영역 좌우를 헤더 제목·푸터 기준선(frameInset)에 수직 정렬 */}
        <div
          className={`pv4-columns pv4-problem-cols${renewalB4 ? (first ? ' pv4-b4-first' : ' pv4-b4-rest') : ''}`}
          style={{ height: `${first ? geo.areaFirstH : geo.areaRestH}mm`, margin: `0 ${geo.frameInset}mm` }}
        >
          {[
            { col: 1, items: page.left },
            { col: 2, items: page.right },
          ].map(({ col, items }) => (
            <div key={col} className="pv4-column">
              {items?.map((p) => {
                // 마커가 지정된 문제: 원래 슬롯 위치에 박스를 놓고 문제를 밴드만큼 내린다.
                // 단, 배치(measureV4)는 마커를 모르므로 내릴 여유가 없을 수 있다 —
                // 내리면 문제영역을 넘치는 경우(주로 하단 슬롯의 긴 문제)에는 문제를 두고
                // 박스를 슬롯 위쪽 여백에 얹어 프레임·푸터 침범을 막는다.
                const markerLabel = problemMarkers[p.problemNumber];
                const shiftFits = p.startHeight + B4_MARKER_BAND + (p.height || 0) <= areaH;
                const shift = markerLabel && shiftFits ? B4_MARKER_BAND : 0;
                const boxTop = shift ? p.startHeight : Math.max(0, p.startHeight - B4_MARKER_BAND);
                return (
                  <React.Fragment key={p.problem_id}>
                    {markerLabel && (
                      <div style={{ position: 'absolute', top: `${boxTop}mm`, left: 0 }}>
                        <B4MarkerBox label={markerLabel} />
                      </div>
                    )}
                    <ProblemItemV4 problem={p} geo={geo} extraTop={shift} />
                  </React.Fragment>
                );
              })}
            </div>
          ))}
        </div>

        {renewalB4 ? (
          <FooterRenewalB4 geo={geo} pageNo={pageIndex + 1} totalPages={totalProblemPages} />
        ) : renewalA4 ? (
          <FooterRenewal geo={geo} pageNo={pageIndex + 1} edu_ver={meta.edu_ver} tree_name={meta.tree_name} />
        ) : (
          <Footer geo={geo} pageNo={pageIndex + 1} leftFooter={meta.leftFooter} edu_ver={meta.edu_ver} tree_name={meta.tree_name} />
        )}
      </div>
    </div>
  );
}

function AnswerPage({ rows, pageNo, geo, meta }) {
  return (
    <div className="pv4-page">
      <div className="pv4-content-box" style={{ fontFamily: "'Pretendard', sans-serif" }}>
        <div style={{ paddingTop: `${8 * geo.k}mm` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <p style={{ fontSize: `${geo.pt(14)}pt`, margin: 0 }}>{meta.idLabel || 'TID'}. {meta.exam_id}</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: `${4 * geo.k}mm` }}>
            <h1 style={{ fontSize: `${geo.pt(28)}pt`, fontWeight: 900, margin: 0, color: '#4ABC85' }}>빠른정답</h1>
            <div>
              {meta.student_name && <span style={{ fontSize: `${geo.pt(11)}pt`, fontWeight: 'bold', marginRight: `${2 * geo.k}mm` }}>{meta.student_name}</span>}
              <span style={{ fontSize: `${geo.pt(11)}pt`, color: '#666' }}>{meta.edu_ver} · {meta.tree_name}</span>
            </div>
          </div>
          <hr style={{ border: 'none', borderTop: '2px solid #333', margin: `${4 * geo.k}mm 0 0 0` }} />
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: `${4 * geo.k}mm` }}>
          <thead>
            <tr>
              <th style={{ width: '18%', textAlign: 'left', padding: `${2 * geo.k}mm 0`, fontSize: `${geo.contentFontPt}pt`, borderBottom: '1px solid #999' }}>문제 번호</th>
              <th style={{ width: '18%', textAlign: 'center', padding: `${2 * geo.k}mm 0`, fontSize: `${geo.contentFontPt}pt`, borderBottom: '1px solid #999' }}>PID</th>
              <th style={{ textAlign: 'center', padding: `${2 * geo.k}mm 0`, fontSize: `${geo.contentFontPt}pt`, borderBottom: '1px solid #999' }}>정답</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((answer) => (
              <tr key={answer.problem_id} style={{ borderBottom: '1px solid #E5E5E5' }}>
                <td style={{ padding: `${1.6 * geo.k}mm 0`, fontSize: `${geo.contentFontPt}pt`, fontWeight: 700, color: '#4ABC85' }}>{pad2(answer.number)}</td>
                <td style={{ padding: `${1.6 * geo.k}mm 0`, fontSize: `${geo.contentFontPt}pt`, textAlign: 'center', color: '#666' }}>{answer.problem_id}</td>
                <td className="pv4-answer-cell" style={{ padding: `${1.6 * geo.k}mm 0`, textAlign: 'center' }}>
                  <div
                    className="pv4-problem-content"
                    style={{ margin: 0, whiteSpace: 'nowrap', fontSize: `${geo.contentFontPt}pt`, display: 'inline-block' }}
                    dangerouslySetInnerHTML={{ __html: answer.answer }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <Footer geo={geo} pageNo={pageNo} leftFooter={meta.leftFooter} edu_ver={meta.edu_ver} tree_name={meta.tree_name} />
      </div>
    </div>
  );
}

/**
 * 풀이 항목 — 문제 항목과 번호 타이포를 공유한다(numberSpecV4). 흐름 배치(absolute 아님)라
 * 분배는 distributeSolutionsV4 가 잰 높이로 좌→우 그리디로 채운다.
 * 이 마크업을 바꾸면 measureV4.measureSolutionHeightMm 도 같이 바꿔야 실측이 맞는다.
 */
/**
 * 표지(수능형 B4 앞장) — 실물 시안 실측 배치.
 *
 * 시안에서 읽은 것 그대로: 제2교시 캡슐 / 상단 줄 / 대제목 / 성명·수험번호 칸 /
 * 유의사항 상자(필적확인 회색 박스 포함) / 과목·쪽수 상자 / 하단 회색 띠 / 학원 로고.
 *
 * 값의 출처를 한 곳으로 묶는다 — 상단 줄과 대제목은 **문제 면 헤더와 같은 meta**(b4Line1·
 * b4Title·b4Subject)를 쓴다. 표지에만 따로 입력칸을 두면 같은 시험지가 앞뒤로 다른 이름을
 * 달게 된다. 학원 로고·폰트도 시험지가 쓰는 것을 그대로 재사용한다(logo_academy.png,
 * KoPubWorldBatang / HanamDaum / NanumSquareRound). 예외는 본문 서체 — 유의사항·※ 줄·회색
 * 띠·필적확인 문구는 고딕(KoPubWorldDotum), 성명·수험번호 칸은 NanumSquareRound, 과목·쪽수
 * 줄은 HanamDaum 이다(오너 시안 매칭 2026-09-09).
 *
 * 쪽수는 세지 않고 **실제 문제 면 수**에서 만든다(1~N쪽). 손으로 적으면 배치가 바뀔 때마다 거짓이 된다.
 * 표지 자신은 번호를 갖지 않는다 — 문제 1면이 계속 「1」이다(수능 관례이자, 기존 번호를 건드리지 않는 길).
 */

/** 유의사항 — 수능 표준 문구(시험지별로 달라지지 않아 상수로 둔다) */
const B4_COVER_NOTICES = [
  ['문제지의 해당란에 성명과 수험 번호를 정확히 쓰시오.'],
  ['답안지의 필적 확인란에 다음의 문구를 정자로 기재하시오.', 'PHRASE'],
  ['답안지의 해당란에 성명과 수험 번호를 쓰고, 또 수험 번호,', '문형(홀수/짝수), 답을 정확히 표시하시오.'],
  ["단답형 답의 숫자에 ‘0’이 포함되면 그 ‘0’도 답란에 반드시 표시하시오."],
  ['문항에 따라 배점이 다르니, 각 물음의 끝에 표시된 배점을 참고하시오.', '배점은 2점, 3점 또는 4점입니다.'],
  ['계산은 문제지의 여백을 활용하시오.'],
];

const B4_COVER_BOTTOM = '※ 시험이 시작되기 전까지 표지를 넘기지 마시오.';

/**
 * 표지 과목·쪽수 — **실물 표지 그대로 고정**(오너 지시 2026-08-31 "원본 사진에 있던 거 그대로").
 *
 * 우리 시험지 구성과는 무관하다. 실물 표지는 응시자가 자기 과목 쪽수를 찾는 안내지이지,
 * 이 시험지에 무엇이 몇 쪽 들어 있는지의 목록이 아니다 — 그래서 우리가 만든 면 수로 세지 않는다.
 * 바꿀 일이 생기면 이 표 한 곳만 고치면 된다.
 *
 * ※ 기하 17~20쪽만 사진에 없다(사진은 확률과 통계·미적분 두 줄까지). 앞 두 줄이 4쪽씩
 *   이어지는 규칙을 그대로 이었다.
 */
/** pt → mm. 들여쓰기를 「글자 몇 자분」으로 잡을 때 쓴다 */
const PT_TO_MM = 25.4 / 72;

/**
 * 표지 본문 글자 배수(오너 지시 2026-08-31) — 성명·수험번호 칸, 유의사항 상자 안,
 * 하단 회색 띠에 함께 건다. 한 곳에서 잡아야 셋이 같은 크기로 움직인다.
 */
const COVER_TEXT_SCALE = 1.2;

/** 상단 줄(b4Line1)만 따로 키운다 — 대제목과의 크기 차가 실물보다 컸다(오너 지시) */
const COVER_LINE1_SCALE = 1.5;

/**
 * 덩이(성명·수험번호 / 유의사항 / 과목·쪽수 / 하단 띠) 사이 간격.
 * 절대 좌표로 각각 놓으면 상자 높이가 바뀔 때마다 간격이 제멋대로 벌어진다 —
 * 한 흐름에 세우고 이 값 하나로 사이를 똑같이 띄운다.
 *
 * 10 → 8, 그리고 상단 줄↔대제목만 따로 좁힌다(COVER_TITLE_GAP — 오너 지시 2026-09-09).
 * 「아래」 줄 자리를 **입력 유무와 무관하게 항상** 흐름에 두기로 하면서(자리가 생겼다 없어지면
 * 아래 덩이들이 매번 출렁인다) 그 높이만큼 간격에서 되찾아야 로고(paper 330)를 안 덮는다.
 * 종전의 「여섯 사이 전부 같게」(2026-08-31)는 이 지시로 대체됐다 — 제목 덩이 안(상단 줄·
 * 대제목·아래 줄)은 헤더처럼 붙여 세우고, 덩이 사이만 이 값으로 균일하게 띄운다.
 */
const COVER_BLOCK_GAP = 8;

/**
 * 상단 줄(b4Line1)↔대제목 사이 — 덩이 간격보다 좁다(오너 지시 2026-09-09 "간격 좁히고").
 * 문제 면 헤더에서도 이 세 줄은 한 제목 묶음으로 붙어 있다 — 표지도 같은 결로 세운다.
 */
const COVER_TITLE_GAP = 4;

/** 과목 줄 기준 글자 크기. 공통·선택과목 머리줄은 ×1.5, 개별 선택과목은 ×1.2(오너 지시 2026-08-31) */
const COVER_TOC_BASE_PT = 15;
const COVER_TOC_HEAD_SCALE = 1.5;
const COVER_TOC_ITEM_SCALE = 1.2;

/**
 * 개별 선택과목 들여쓰기 — 「○ 선택과목」의 **「택」 밑에서** 시작한다(오너 지시).
 * 머리줄 글자로 ○(1자) + 사이 공백(0.5자) + 선(1자) = 2.5자를 건너뛴 자리다.
 * 머리줄 배수가 바뀌면 이 값도 따라 움직인다 — 고정 mm 로 박으면 그때 어긋난다.
 */
const COVER_ELECTIVE_INDENT_MM = 2.5 * (15 * 1.5) * PT_TO_MM;

const B4_COVER_COMMON = { label: '○ 공통과목', pages: '1~8쪽' };
const B4_COVER_ELECTIVES = [
  { label: '확률과 통계', pages: '9~12쪽' },
  { label: '미적분', pages: '13~16쪽' },
  { label: '기하', pages: '17~20쪽' },
];

/** 성명·수험번호 칸 — 수험번호는 8칸(4 + 하이픈 + 4)이 시안 모양이다 */
function CoverIdRow({ geo }) {
  /**
   * 칸 높이를 실물에 맞춰 키웠다(7.5 → 11mm 기준).
   *
   * 가운데 정렬: lineHeight 를 칸 높이만큼 주면 **줄 상자가 칸보다 커져** 오히려 위로 밀린다
   * (테두리 0.3mm 두 겹만큼 안쪽이 좁다). 줄 상자를 글자 크기로 눌러 두고(lineHeight:1)
   * flex 로 상하·좌우를 함께 가운데에 놓는다 — 그래야 서체의 위 여백에 끌려가지 않는다.
   */
  const cellH = 11 * COVER_TEXT_SCALE * geo.k;
  const cell = { border: '0.3mm solid #111', height: `${cellH}mm`, boxSizing: 'border-box' };
  /**
   * 수험번호 기입칸 사이는 **점선**이다(실물). 바깥 테두리는 실선이라, 칸을 이어 붙일 때
   * 왼쪽 변만 점선으로 바꾼다 — 첫 칸의 왼쪽은 「수험 번호」 라벨과 맞닿는 바깥선이라 그대로 둔다.
   */
  const digit = {
    ...cell,
    width: `${9 * COVER_TEXT_SCALE * geo.k}mm`,
    borderLeft: '0.3mm dashed #111',
  };
  const centered = {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    textAlign: 'center', lineHeight: 1,
  };
  const label = {
    ...cell,
    ...centered,
    padding: `0 ${4 * COVER_TEXT_SCALE * geo.k}mm`, fontSize: `${15 * COVER_TEXT_SCALE}pt`, fontWeight: 700, whiteSpace: 'nowrap',
  };
  // 서체는 오너 시안(2026-09-09) 매칭 — 이 칸만 고딕(NanumSquareRound), 700은 B 파일이 있다
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', justifyContent: 'center', gap: `${4 * geo.k}mm`, fontFamily: "'NanumSquareRound', 'Pretendard', sans-serif" }}>
      <div style={{ display: 'flex' }}>
        <div style={label}>성명</div>
        <div style={{ ...cell, ...centered, width: `${44 * COVER_TEXT_SCALE * geo.k}mm`, borderLeft: 'none' }} />
      </div>
      <div style={{ display: 'flex' }}>
        <div style={label}>수험 번호</div>
        {/* 라벨과 맞닿는 첫 칸만 실선(바깥 테두리), 나머지 칸 사이는 점선 */}
        {[0, 1, 2, 3].map((i) => (
          <div key={`a${i}`} style={{ ...digit, ...centered, ...(i === 0 ? { borderLeft: 'none' } : null) }} />
        ))}
        {/* 하이픈 칸 — 글자가 변에 닿지 않게 좌우로 늘린다. 양옆은 칸 구분이 아니라 표기라 실선 */}
        <div style={{ ...cell, ...centered, width: `${11 * COVER_TEXT_SCALE * geo.k}mm`, borderLeft: 'none', fontSize: `${14 * COVER_TEXT_SCALE}pt` }}>—</div>
        {[0, 1, 2, 3].map((i) => (
          <div key={`b${i}`} style={{ ...digit, ...centered, ...(i === 0 ? { borderLeft: 'none' } : null) }} />
        ))}
      </div>
    </div>
  );
}

/**
 * 점선 지시선 + 오른쪽 쪽수 — 「공통과목 ······ 1~8쪽」
 * 쪽수가 없는 줄(「○ 선택과목」 머리줄)에는 지시선을 긋지 않는다 — 가리킬 곳이 없는 선이다.
 */
function CoverTocRow({ geo, label, pages, indentMm = 0, scale = 1 }) {
  const hasPages = String(pages ?? '').trim() !== '';
  // 과목 이름만 키운다(오너 지시) — 쪽수는 실물처럼 이름보다 작게 둔다.
  // 줄 간격도 함께 벌린다: 글자만 키우면 큰 줄끼리 서로 붙어 보인다.
  const labelPt = COVER_TOC_BASE_PT * scale;
  return (
    /* 줄 전체(쪽수 포함)가 대제목과 같은 HanamDaum — 한 줄 한 서체(오너 승인 2026-09-09) */
    <div style={{ display: 'flex', alignItems: 'baseline', gap: `${2 * geo.k}mm`, marginTop: `${3 * scale * geo.k}mm`, paddingLeft: `${indentMm}mm`, fontFamily: "'HanamDaum', 'Pretendard', sans-serif" }}>
      <span style={{ fontSize: `${labelPt}pt`, whiteSpace: 'nowrap' }}>{label}</span>
      {hasPages && <span style={{ flex: 1, borderBottom: '0.25mm dotted #333', transform: 'translateY(-0.6mm)' }} />}
      {hasPages && <span style={{ fontSize: '13pt', whiteSpace: 'nowrap' }}>{pages}</span>}
    </div>
  );
}

function CoverPageB4({ geo, meta }) {
  const inner = geo.frameInset;
  const y = (paperMm) => paperMm - geo.inset.top;
  const serif = "'KoPubWorldBatang', 'Pretendard', serif";
  const subject = String(meta?.b4Subject ?? '').trim();
  const line2 = String(meta?.b4Line2 ?? '').trim();

  return (
    <div className="pv4-page">
      <div className="pv4-content-box" style={{ fontFamily: serif }}>
        {/* 제2교시 캡슐 — 문제 첫 장과 같은 규격·같은 자리 */}
        <div style={{ position: 'absolute', left: `${inner}mm`, top: `${y(B4_PERIOD_Y - 20)}mm`, border: `${B4_PERIOD_BORDER}mm solid #111`, borderRadius: '999px', padding: `${B4_PERIOD_PAD_Y}mm 2.5mm`, fontSize: `${B4_PERIOD_FONT_PT}pt`, fontFamily: serif, fontWeight: 700, color: '#111', lineHeight: B4_PERIOD_LINE_H }}>
          제 2교시
        </div>

        {/*
          상단 줄 · 대제목 · 성명·수험번호 / 유의사항 / 과목·쪽수 / 하단 띠 — **전부 한 흐름**이다.
          절대 좌표로 각각 놓으면 글자 배수나 문구 길이가 바뀔 때마다 사이가 제멋대로 벌어진다.
          흐름에 세우고 marginTop 하나(COVER_BLOCK_GAP)로 띄우면 사이가 언제나 같다(오너 지시).
          상단 줄·대제목·아래 줄은 문제 면 헤더와 같은 meta 를 쓴다(b4Line1 · b4Title · b4Subject · b4Line2).
        */}
        <div style={{ position: 'absolute', left: `${inner}mm`, right: `${inner}mm`, top: `${y(24)}mm` }}>
          <div style={{ textAlign: 'center', fontSize: `${17 * COVER_LINE1_SCALE}pt`, fontFamily: "'NanumSquareRound', 'Pretendard', sans-serif", fontWeight: 700, color: '#231F20' }}>
            {meta?.b4Line1 || ''}
          </div>
          <div style={{ marginTop: `${COVER_TITLE_GAP * geo.k}mm`, textAlign: 'center', fontSize: '40pt', fontFamily: "'HanamDaum', 'Pretendard', sans-serif", fontWeight: 400, color: '#111', lineHeight: 1.1 }}>
            {meta?.b4Title ?? '수학 영역'}{subject ? <span style={{ fontSize: '20pt' }}>({subject})</span> : null}
          </div>

          {/* 아래 줄(b4Line2) — 문제 면 헤더의 셋째 줄과 같은 얼굴(회색·굵게), 크기만 표지
              본문 배수(COVER_TEXT_SCALE)로 키운다. **입력이 비어도 자리를 항상 남긴다**(오너 지시
              2026-09-09) — 있다 없다에 따라 아래 덩이 전부가 오르내리면 간격을 아무리 재도
              로고와의 사이가 두 벌이 된다. 빈 값은 nbsp 로 줄 높이만 세운다. */}
          <div style={{ marginTop: `${2 * geo.k}mm`, textAlign: 'center', fontSize: `${16 * COVER_TEXT_SCALE}pt`, fontFamily: "'NanumSquareRound', 'Pretendard', sans-serif", fontWeight: 700, color: '#A3A1A2' }}>
            {line2 || '\u00A0'}
          </div>

          <div style={{ marginTop: `${COVER_BLOCK_GAP * geo.k}mm` }}>
            <CoverIdRow geo={geo} />
          </div>

          {/* 유의사항 상자 — 안쪽 글자를 함께 키운다(COVER_TEXT_SCALE). 본문은 고딕(Dotum, 오너 시안) */}
          <div style={{ marginTop: `${COVER_BLOCK_GAP * geo.k}mm`, border: '0.3mm solid #111', padding: `${7 * geo.k}mm ${8 * geo.k}mm`, fontFamily: "'KoPubWorldDotum', 'Pretendard', sans-serif" }}>
            {B4_COVER_NOTICES.map((lines, i) => (
              <div key={i} style={{ display: 'flex', gap: `${3 * geo.k}mm`, marginTop: i === 0 ? 0 : `${4.5 * geo.k}mm` }}>
                <span style={{ fontSize: `${13 * COVER_TEXT_SCALE}pt`, lineHeight: 1.5, flexShrink: 0 }}>○</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  {lines.map((line, j) => (line === 'PHRASE' ? (
                    /* 필적 확인란 문구 — 회색 박스. 학원마다 바꾸는 자리라 툴바에서 고친다.
                       굵기 700 = Dotum Bold 파일의 무게(800이면 브라우저가 합성 볼드를 끼운다) */
                    <div key={j} style={{ background: '#c9c9c9', border: '0.3mm solid #555', padding: `${1.6 * geo.k}mm ${5 * geo.k}mm`, margin: `${2.5 * geo.k}mm 0 ${1 * geo.k}mm`, textAlign: 'center', fontSize: `${14 * COVER_TEXT_SCALE}pt`, fontWeight: 700, color: '#111' }}>
                      {meta?.b4CoverPhrase || ''}
                    </div>
                  ) : (
                    <p key={j} style={{ margin: 0, fontSize: `${14 * COVER_TEXT_SCALE}pt`, lineHeight: 1.55, color: '#111' }}>{line}</p>
                  )))}
                </div>
              </div>
            ))}
          </div>

          {/* 과목 · 쪽수 상자 — 쪽수는 실물 표지 값 고정(B4_COVER_COMMON · B4_COVER_ELECTIVES) */}
          <div style={{ marginTop: `${COVER_BLOCK_GAP * geo.k}mm`, border: '0.3mm solid #111', padding: `${7 * geo.k}mm ${8 * geo.k}mm` }}>
            <p style={{ margin: 0, fontSize: `${13 * COVER_TEXT_SCALE}pt`, color: '#111', fontFamily: "'KoPubWorldDotum', 'Pretendard', sans-serif" }}>
              ※ 공통과목 및 자신이 선택한 과목의 문제지를 확인하고, 답을 정확히 표시하시오.
            </p>
            <CoverTocRow geo={geo} label={B4_COVER_COMMON.label} pages={B4_COVER_COMMON.pages} scale={COVER_TOC_HEAD_SCALE} />
            <CoverTocRow geo={geo} label="○ 선택과목" pages="" scale={COVER_TOC_HEAD_SCALE} />
            {B4_COVER_ELECTIVES.map((e) => (
              <CoverTocRow key={e.label} geo={geo} label={e.label} pages={e.pages} indentMm={COVER_ELECTIVE_INDENT_MM} scale={COVER_TOC_ITEM_SCALE} />
            ))}
          </div>

          {/* 하단 회색 띠 — 필적확인 박스와 같은 Dotum Bold(700 = Bold 파일 무게) */}
          <div style={{ marginTop: `${COVER_BLOCK_GAP * geo.k}mm`, background: '#c9c9c9', border: '0.3mm solid #555', padding: `${2.4 * geo.k}mm 0`, textAlign: 'center', fontSize: `${16 * COVER_TEXT_SCALE}pt`, fontWeight: 700, color: '#111', fontFamily: "'KoPubWorldDotum', 'Pretendard', sans-serif" }}>
            {B4_COVER_BOTTOM}
          </div>
        </div>

        {/* 학원 로고 — 시험지 푸터가 쓰는 것 그대로 */}
        <img
          src={`${process.env.PUBLIC_URL}/assets/images/print_v4/logo_academy.png`}
          alt="학원 로고"
          style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', top: `${y(330)}mm`, height: `${7 * geo.k}mm`, width: 'auto' }}
        />
      </div>
    </div>
  );
}

// 풀이 본문이 없을 때의 글 — 측정(measureV4)과 렌더가 같은 글을 쓴다. 실패를 「풀이 없음」으로 찍지 않는다
export const solutionPlaceholder = (sol) => (sol.failed ? '풀이를 불러오지 못함' : '풀이 없음');
// 풀이와 풀이 사이 여백(mm) — 렌더와 배치 계산(distributeSolutionsV4)의 단일 출처. 값은 v2 8px 를 용지 비례로 환산한 것
export const solutionItemGapMm = (geo) => 3 * geo.k;

function SolutionItemV4({ sol, geo }) {
  const { fontPt, font, weight, color, text, gapMm } = numberSpecV4(geo, sol.number);
  const body = (
    <div style={{ display: 'flex', gap: `${gapMm}mm`, alignItems: 'flex-start', marginBottom: `${solutionItemGapMm(geo)}mm`, paddingBottom: `${2 * geo.k}mm`, borderBottom: '1px solid #eee' }}>
      <div style={{ fontSize: `${fontPt}pt`, fontWeight: weight, color, lineHeight: 1, minWidth: `${fontPt}pt`, whiteSpace: 'nowrap', fontFamily: font }}>
        {text}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {sol.content ? (
          <div className="pv4-problem-content" style={{ fontSize: `${geo.contentFontPt}pt`, overflowWrap: 'break-word', wordBreak: 'break-word' }} dangerouslySetInnerHTML={{ __html: sol.content }} />
        ) : (
          /* 측정(measureV4 solutionMeasureHtml)과 같은 틀 — 줄 높이를 물려받으면 주변 값에 따라 측정과 어긋나 단이 넘친다(B4 1.3px 실측) */
          <div style={{ fontSize: `${geo.contentFontPt}pt`, lineHeight: 1.5 }}>
            <span style={{ color: '#999', fontSize: `${geo.contentFontPt}pt` }}>{solutionPlaceholder(sol)}</span>
          </div>
        )}
      </div>
    </div>
  );
  if (!sol.slice) return body;
  /**
   * 단보다 긴 풀이의 창(distributeSolutionsV4 참고) — 같은 본문을 창마다 통째로 그리고
   * offset 만큼 올려, 이 창에 해당하는 세로 구간만 내보인다. 본문 마크업이 측정과 동일하므로
   * 창들을 이어 붙이면 정확히 원본 한 벌이다(끊긴 줄도 다음 창 첫머리에 이어진다).
   * 구분선·마감 여백은 본문 맨 아래에 있어 마지막 창에만 자연히 나타난다 — 앞 창의 창틀에는
   * 항목 사이 여백(marginBottom)이 없어야 다음 창과 좌표가 이어진다.
   */
  const isLast = sol.slice.index === sol.slice.count - 1;
  return (
    <div style={{ height: `${sol.height}mm`, overflow: 'hidden', marginBottom: isLast ? `${solutionItemGapMm(geo)}mm` : 0 }}>
      <div style={{ marginTop: `${-sol.slice.offset}mm` }}>{body}</div>
    </div>
  );
}

/**
 * 풀이 면 — 문제 면의 "이후 장"과 같은 크롬을 쓴다(디자인별 헤더·프레임·푸터).
 * 예전엔 v2 를 그대로 옮긴 구버전 머리("풀이" 녹색 제목 + TID)와 구 푸터를 썼는데,
 * 문제 면만 리뉴얼(renewalA4/renewalB4)로 바뀌어 한 권 안에서 두 디자인이 섞였다.
 * 면 표시는 헤더에 작은 「풀이」 라벨로만 한다 — 큰 제목 밴드는 리뉴얼 시안에 없다.
 */
function SolutionPage({ page, pageNo, totalPages, geo, meta }) {
  const renewalA4 = geo.design === 'renewalA4';
  const renewalB4 = geo.design === 'renewalB4';
  const inner = geo.frameInset;
  const renderCol = (items) => (
    <div className="pv4-column">
      {items.map((sol) => <SolutionItemV4 key={sol.slice ? `${sol.problem_id}:${sol.slice.index}` : sol.problem_id} sol={sol} geo={geo} />)}
    </div>
  );
  return (
    <div className="pv4-page">
      <div className="pv4-content-box" style={{ fontFamily: "'Pretendard', sans-serif" }}>
        {renewalB4 ? (
          <HeaderRenewalB4Rest geo={geo} pageNo={pageNo} meta={meta} titleSuffix="풀이" />
        ) : renewalA4 ? (
          /* A4 이후 장 헤더는 빈 띠다 — 그 안에 면 라벨만 리뉴얼 캡션 규격(10pt #888)으로 둔다 */
          <div style={{ position: 'relative', height: `${geo.headerRestH + geo.restTopGap}mm` }}>
            <div style={{ position: 'absolute', left: `${inner}mm`, bottom: `${geo.restTopGap}mm`, fontSize: '10pt', color: '#888' }}>
              풀이 · {meta.tree_name}
            </div>
            <div style={{ position: 'absolute', right: `${inner}mm`, bottom: `${geo.restTopGap}mm`, fontSize: '10pt', color: '#888' }}>
              {meta.idLabel || 'TID'}. {meta.exam_id}
            </div>
          </div>
        ) : (
          <div style={{ height: `${geo.headerRestH}mm`, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <span style={{ fontSize: `${geo.pt(16)}pt`, fontWeight: 900, color: '#4ABC85' }}>풀이</span>
            <span style={{ fontSize: `${geo.pt(11)}pt`, color: '#666' }}>{meta.idLabel || 'TID'}. {meta.exam_id}</span>
          </div>
        )}

        {/* 문제 면과 같은 클래스·여백 — 세로 구분선이 프레임·푸터 줄과 같은 규격으로 이어진다 */}
        <div
          className={`pv4-columns pv4-problem-cols${renewalB4 ? ' pv4-b4-rest' : ''}`}
          style={{
            height: `${geo.areaRestH}mm`,
            overflow: 'hidden',
            ...(renewalA4 || renewalB4
              ? { margin: `0 ${inner}mm` }
              : { marginTop: `${geo.restTopGap}mm` }),
          }}
        >
          {renderCol(page.left || [])}
          {renderCol(page.right || [])}
        </div>

        {renewalB4 ? (
          <FooterRenewalB4 geo={geo} pageNo={pageNo} totalPages={totalPages} />
        ) : renewalA4 ? (
          <FooterRenewal geo={geo} pageNo={pageNo} edu_ver={meta.edu_ver} tree_name={meta.tree_name} />
        ) : (
          <Footer geo={geo} pageNo={pageNo} leftFooter={meta.leftFooter} edu_ver={meta.edu_ver} tree_name={meta.tree_name} />
        )}
      </div>
    </div>
  );
}

const ANSWER_ROWS_PER_PAGE = 25;

/**
 * @param {object} props
 * @param {Array}  props.problemPages  distributeProblemsV4 결과
 * @param {Array}  props.answers      [{number, problem_id, answer(html)}]
 * @param {Array}  props.solutionPages distributeSolutionsV4 결과
 * @param {object} props.geo           getSheetGeometry 결과
 * @param {object} props.meta          { tree_name, edu_ver, student_name, exam_id, leftFooter, idLabel? } — idLabel 은 번호 앞 라벨(기본 'TID', 오답노트는 'NOTE')
 */
function PrintSheetV4({ problemPages, answers, solutionPages, geo, meta, renderPageOverlay }) {
  // v2 규칙 유지: 문제 페이지가 홀수면 양면 인쇄용 빈 페이지 1장
  const needEmpty = problemPages.length % 2 !== 0;
  const answerChunks = [];
  for (let i = 0; i < answers.length; i += ANSWER_ROWS_PER_PAGE) {
    answerChunks.push(answers.slice(i, i + ANSWER_ROWS_PER_PAGE));
  }

  return (
    <div className="pv4-sheet">
      <SheetChrome geo={geo} />
      {/* 표지는 수능형(B4)에만 있고 번호를 갖지 않는다 — 문제 1면이 계속 「1」이다 */}
      {geo.design === 'renewalB4' && meta?.b4Cover && (
        <CoverPageB4 geo={geo} meta={meta} />
      )}
      {problemPages.map((page, i) => (
        <ProblemPage key={`p-${i}`} page={page} pageIndex={i} geo={geo} meta={meta} totalProblemPages={problemPages.length} renderPageOverlay={renderPageOverlay} />
      ))}
      {needEmpty && <div className="pv4-page" aria-hidden="true" />}
      {answerChunks.map((rows, i) => (
        <AnswerPage key={`a-${i}`} rows={rows} pageNo={i + 1} geo={geo} meta={meta} />
      ))}
      {solutionPages.map((page, i) => (
        <SolutionPage key={`s-${i}`} page={page} pageNo={i + 1} totalPages={solutionPages.length} geo={geo} meta={meta} />
      ))}
    </div>
  );
}

export default PrintSheetV4;
